import { motion } from "framer-motion";
import { Star, Quote, ExternalLink } from "lucide-react";
import { REVIEWS, COMPANY } from "../data/content";
import { BarkWordmark } from "../components/BrandMarks";

export default function Reviews() {
    return (
        <section
            id="reviews"
            data-testid="reviews-section"
            className="relative bg-haas-sand py-24 sm:py-32 overflow-hidden"
        >
            <div className="pointer-events-none absolute top-10 right-10 w-32 h-32 rounded-full bg-haas-sun/50 blur-2xl" />
            <div className="pointer-events-none absolute bottom-10 left-10 w-40 h-40 rounded-full bg-haas-coral/30 blur-2xl" />

            <div className="relative max-w-7xl mx-auto px-5 sm:px-10">
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.4 }}
                    transition={{ duration: 0.6 }}
                    className="text-center max-w-3xl mx-auto mb-16"
                >
                    <span className="chip">⭐ Words from real people</span>
                    <h2 className="mt-6 font-display font-bold text-4xl sm:text-6xl tracking-tight text-haas-ink leading-[1.05]">
                        Nicer than{" "}
                        <span className="marker-highlight">us bragging</span>{" "}
                        about us.
                    </h2>
                </motion.div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
                    {REVIEWS.map((r, i) => (
                        <motion.figure
                            key={r.id}
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.2 }}
                            transition={{ duration: 0.6, delay: i * 0.1 }}
                            data-testid={`review-card-${r.id}`}
                            className={`hard-card p-8 sm:p-10 relative ${
                                i % 2 === 1 ? "md:translate-y-6" : ""
                            }`}
                        >
                            <div className="absolute -top-5 left-8 w-12 h-12 rounded-full bg-haas-sun border-2 border-haas-ink flex items-center justify-center shadow-[3px_3px_0_#123D28]">
                                <Quote className="w-5 h-5 text-haas-ink" />
                            </div>

                            <div
                                className="flex gap-1 mb-5 mt-2"
                                data-testid={`review-rating-${r.id}`}
                            >
                                {Array.from({ length: r.rating }).map(
                                    (_, idx) => (
                                        <Star
                                            key={idx}
                                            className="w-4 h-4 text-haas-goldDeep fill-haas-gold"
                                        />
                                    )
                                )}
                            </div>

                            <blockquote className="font-display font-medium text-xl sm:text-2xl text-haas-ink leading-snug mb-6">
                                "{r.text}"
                            </blockquote>

                            <figcaption className="pt-5 border-t-2 border-dashed border-haas-ink/15 flex items-center justify-between">
                                <div>
                                    <div className="text-haas-ink font-bold">
                                        {r.name}
                                    </div>
                                    <div className="text-haas-goldDeep text-xs uppercase tracking-[0.18em] mt-1 font-bold">
                                        {r.location}
                                    </div>
                                </div>
                                <div className="font-hand text-haas-goldDeep text-2xl -rotate-3">
                                    thank you!
                                </div>
                            </figcaption>
                        </motion.figure>
                    ))}
                </div>

                {/* Trusted-on banner: Bark 5-star reviews */}
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                    data-testid="trust-banner"
                    className="mt-14 sm:mt-20"
                >
                    <div className="text-center mb-8">
                        <span className="chip">⭐ Verified 5-star reviews</span>
                        <h3 className="mt-5 font-display font-bold text-3xl sm:text-4xl text-haas-ink leading-tight">
                            Rated{" "}
                            <span className="marker-highlight">five stars</span>{" "}
                            on Bark.
                        </h3>
                    </div>

                    <div className="grid grid-cols-1 gap-5 max-w-2xl mx-auto">
                        {/* Bark card */}
                        <motion.a
                            href={COMPANY.barkUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="reviews-bark-cta"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.3 }}
                            transition={{ duration: 0.5 }}
                            className="group block rounded-3xl bg-[#0E3220] hover:bg-[#17472E] text-white border-2 border-haas-ink shadow-[8px_8px_0_#123D28] p-7 sm:p-9 transition-colors"
                        >
                            <div className="flex items-center gap-3 mb-5">
                                <BarkWordmark className="h-7 w-auto" />
                                <span className="text-haas-sun text-[10px] font-bold uppercase tracking-[0.22em]">
                                    5★ Reviews
                                </span>
                            </div>
                            <div
                                className="flex gap-1 mb-4"
                                data-testid="reviews-bark-stars"
                            >
                                {Array.from({ length: 5 }).map((_, i) => (
                                    <Star
                                        key={i}
                                        className="w-5 h-5 text-haas-gold fill-haas-gold"
                                    />
                                ))}
                            </div>
                            <div className="font-display font-bold text-xl sm:text-2xl leading-tight">
                                Bark 5★ Reviews
                            </div>
                            <p className="mt-3 text-white/75 text-sm leading-relaxed">
                                Independently verified customer reviews from
                                real landscaping jobs across Coventry &amp; the
                                West Midlands.
                            </p>
                            <div
                                data-testid="reviews-bark-button"
                                className="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-haas-gold text-haas-ink border-2 border-haas-ink font-bold text-sm shadow-[3px_3px_0_#123D28] group-hover:translate-x-1 transition-transform"
                            >
                                See us on Bark
                                <ExternalLink className="w-4 h-4" />
                            </div>
                        </motion.a>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
